import { create } from 'zustand'

/**
 * Cross-cutting guard for unsaved task Description edits when *switching* the
 * task shown in a detail panel (clicking another row, auto-advance, etc.).
 *
 * The panel is keyed by task id, so switching remounts it — the old dirty
 * buffer is gone before the panel can react. So selection sites must consult
 * this store BEFORE they change the selection, and a single app-level dialog
 * (mounted once, outside any panel) resolves the choice.
 *
 * Close / Escape / route-navigation are handled inside the panel itself; this
 * store is only for the same-panel task swap.
 */
interface TaskDescriptionGuardStore {
  /** Whether the active panel's description has unsaved edits. */
  isDirty: boolean
  /** Persists the active panel's live description buffer. */
  saver: (() => Promise<void>) | null

  /** Dialog visibility + the deferred switch action. */
  open: boolean
  pending: (() => void) | null
  /** Whether a "Save & close" is currently in flight. */
  saving: boolean

  /** Called by the active panel as its dirty/saver state changes. */
  setDirty: (dirty: boolean) => void
  registerSaver: (saver: (() => Promise<void>) | null) => void
  /** Called when the active panel unmounts. */
  reset: () => void

  /**
   * Selection sites call this instead of switching directly. Runs `proceed`
   * immediately when clean; otherwise opens the dialog and defers it.
   */
  requestSwitch: (proceed: () => void) => void

  // Dialog actions
  keepEditing: () => void
  discard: () => void
  saveAndProceed: () => Promise<void>
}

export const useTaskDescriptionGuardStore = create<TaskDescriptionGuardStore>(
  (set, get) => ({
    isDirty: false,
    saver: null,
    open: false,
    pending: null,
    saving: false,

    setDirty: dirty => set({ isDirty: dirty }),
    registerSaver: saver => set({ saver }),
    reset: () =>
      set({ isDirty: false, saver: null, open: false, pending: null, saving: false }),

    requestSwitch: proceed => {
      if (!get().isDirty) {
        proceed()
        return
      }
      set({ open: true, pending: proceed })
    },

    keepEditing: () => {
      if (get().saving) return
      set({ open: false, pending: null })
    },

    discard: () => {
      if (get().saving) return
      const { pending } = get()
      set({ isDirty: false, open: false, pending: null })
      pending?.()
    },

    saveAndProceed: async () => {
      const { saver, pending, saving } = get()
      if (saving) return
      set({ saving: true })
      try {
        await saver?.()
      } catch {
        // saver already surfaces the error; keep the dialog open on failure.
        set({ saving: false })
        return
      }
      set({ isDirty: false, open: false, pending: null, saving: false })
      pending?.()
    },
  }),
)
